import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import { colors } from "../../styles/colors";
import { down } from "../../styles/breakpoints";
import { pageShellRoot, tablePaginationResponsive } from "../../styles/pageShell";

const useStyles = makeStyles((theme: Theme) => ({
  /*
   * `contentMain` and `vehiculeRoot` were the wrong way round here: the page
   * padding sat on the *shell* (which the sidebar sizes) rather than on the
   * scrolling content, so the padding scrolled with the page and the
   * `minHeight: 100vh` added a viewport-worth of blank space below the fold.
   */
  contentMain: pageShellRoot,

  /* No width here: this class lands on <main>, which already carries an
     inline margin-left for the sidebar. See pageShell.ts. */
  vehiculeRoot: {
    minWidth: 0,
    padding: "24px",
    backgroundColor: theme?.palette?.background?.default || colors.bgcolor,
    [down.md]: { padding: "20px 16px" },
    [down.sm]: { padding: "16px 12px" },
    ...tablePaginationResponsive,
  },

  /*
   * 1100px, not 800px.
   *
   * The établissements table has four columns and a pagination toolbar that
   * needs roughly 420px on one line. Capping the page at 800px squeezed the
   * table while leaving most of a wide screen empty — the worst of both. The
   * settings list is short and reads fine at this width.
   */
  pageContainer: {
    maxWidth: 1100,
    width: "100%",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },

  /*
   * Flat, bordered surfaces.
   *
   * These used to carry a 32px blur shadow and lift 2px on hover. Neither card
   * is interactive, so the lift signalled a click target that does not exist,
   * and two heavy shadows on one screen made a settings page feel like a
   * dashboard. A 1px border separates them from the background at a fraction
   * of the visual cost.
   */
  card: {
    "&.MuiPaper-root": {
      borderRadius: 12,
      overflow: "hidden",
      backgroundColor: theme?.palette?.background?.paper || colors.WHITE,
    },
  },

  cardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "14px 16px",
    borderBottom: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
  },

  /*
   * Rows are separated by a divider instead of each being its own tinted,
   * bordered, rounded box. The old treatment gave every setting a card inside
   * a card, which is what made two switches occupy most of the viewport.
   *
   * The `translateX(4px)` hover is gone too: the row is not clickable, and
   * text that slides sideways under the cursor is noise, not feedback.
   */
  settingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: "14px 16px",
    minHeight: 56,
    [down.sm]: {
      alignItems: "flex-start",
      padding: "12px 14px",
    },
  },

  headCell: {
    "&.MuiTableCell-root": {
      fontSize: 12,
      fontWeight: 600,
      color: theme?.palette?.text?.secondary || "#666",
      backgroundColor: theme?.palette?.grey?.[50] || "#fafafa",
      whiteSpace: "nowrap",
    },
  },

  pagination: {
    "&.MuiTablePagination-root": {
      borderTop: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
      /*
       * This screen renders TablePagination directly rather than through
       * CustomTable, so it never picked up the wrapping rules added there.
       * The toolbar needs roughly 420px on a single line and overflowed the
       * card below that width. The "rows per page" label is dropped on phones
       * because the selector beside it already carries the meaning.
       */
      minWidth: 0,
      overflow: "hidden",
      "& .MuiTablePagination-toolbar": {
        flexWrap: "wrap",
        justifyContent: "flex-end",
        rowGap: 4,
        minHeight: "auto",
        paddingLeft: 0,
      },
      "& .MuiTablePagination-displayedRows": { margin: 0 },
      "& .MuiTablePagination-selectLabel": {
        margin: 0,
        display: "block",
        [down.sm]: { display: "none" },
      },
    },
  },
}));

export default useStyles;
